import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberDeactivationCheckDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useDialogFocus } from '../../hooks/useDialogFocus';

/** Both status-edit entry points use the same read-only balance confirmation. */
export function useMemberDeactivationConfirm(memberId: string | undefined) {
  const { t } = useTranslation('member');
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MemberDeactivationCheckDto | null>(null);
  const [error, setError] = useState('');
  const resolve = useRef<((confirmed: boolean) => void) | null>(null);
  const dialogRef = useDialogFocus(open);
  useEffect(
    () => () => {
      resolve.current?.(false);
    },
    [],
  );
  const finish = (confirmed: boolean) => {
    resolve.current?.(confirmed);
    resolve.current = null;
    setOpen(false);
  };
  const ask = async () => {
    if (!memberId || resolve.current) return false;
    setData(null);
    setError('');
    setOpen(true);
    const answer = new Promise<boolean>((done) => {
      resolve.current = done;
    });
    try {
      setData(await api.get<MemberDeactivationCheckDto>(`/members/${memberId}/deactivation-check`));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('deactivationCheckFailed'));
    }
    return answer;
  };
  const dialog = open && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t('confirmDeactivate')}
        className="bg-base-100 rounded-lg p-5 max-w-lg w-full max-h-[90vh] overflow-auto"
        onKeyDown={(e) => {
          if (e.key === 'Escape') finish(false);
        }}
      >
        <h2 className="font-semibold mb-3">{t('confirmDeactivate')}</h2>
        {!data && !error && <p>{t('common:loading')}</p>}
        {error && (
          <p role="alert" className="text-error">
            {error}
          </p>
        )}
        {!!data?.balances.length && (
          <>
            <p className="mb-3">{t('deactivationBalanceWarning')}</p>
            <ul className="space-y-2">
              {data.balances.map((row, i) => (
                <li key={i} className={Number(row.amount) < 0 ? 'text-error' : ''}>
                  {row.venueName} · {t(`balanceKind${row.kind}`, { defaultValue: row.kind })}{' '}
                  {row.period} · {row.amount}
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="flex justify-end gap-3 mt-4">
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-base-300 text-sm hover:bg-base-300 disabled:opacity-50"
            onClick={() => finish(false)}
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-warning text-warning-content text-sm font-medium disabled:opacity-50"
            disabled={!data}
            onClick={() => finish(true)}
          >
            {t('common:confirm')}
          </button>
        </div>
      </div>
    </div>
  );
  return { ask, dialog, open };
}
